import { Add, Delete, Edit } from '@mui/icons-material';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { PatternFormValues } from 'src/pages/TruckShifts/patternForm';
import { PatternEditorDialog } from 'src/pages/TruckShifts/presenters/PatternEditorDialog.presenter';

type Props = {
  open: boolean;
  submitting: boolean;
  patterns: ShiftPatternEntity[];
  onClose: () => void;
  onSubmitPattern: (values: PatternFormValues, editing?: ShiftPatternEntity) => Promise<boolean>;
  onDeletePattern: (pattern: ShiftPatternEntity) => Promise<boolean>;
};

const slotsLabel = (pattern: ShiftPatternEntity): string =>
  pattern.slots.map((slot) => `${slot.startTime}-${slot.endTime}`).join(' / ');

export const PatternManagerDialog: FC<Props> = ({
  open, submitting, patterns, onClose, onSubmitPattern, onDeletePattern,
}) => {
  const [editing, setEditing] = useState<ShiftPatternEntity | 'new' | null>(null);
  const editingPattern = editing && editing !== 'new' ? editing : undefined;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          便の管理
          <Typography variant="caption" color="text.secondary" component="div">
            名前と時間を自由に登録・編集できます
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 0.5 }}>
            {patterns.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2 }}>便がありません。新規追加してください。</Typography>
            )}
            {patterns.map((pattern) => (
              <Stack
                key={pattern.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.75,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: `color-mix(in srgb, ${pattern.color} 14%, #fff)`,
                }}
              >
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: pattern.color }} />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{pattern.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {slotsLabel(pattern)}
                    {pattern.multiDay ? ` 出っぱなし(${pattern.multiDayLength ?? 2}日)` : ''}
                  </Typography>
                </Stack>
                <IconButton size="small" onClick={() => setEditing(pattern)} aria-label="便を編集">
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => { void onDeletePattern(pattern); }} aria-label="便を削除">
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              variant="outlined"
              startIcon={<Add />}
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => setEditing('new')}
            >
              新しい便を追加
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {editing !== null && (
        <PatternEditorDialog
          open
          initialPattern={editingPattern}
          submitting={submitting}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            const ok = await onSubmitPattern(values, editingPattern);
            if (ok) setEditing(null);
          }}
          onDelete={editingPattern ? () => {
            void onDeletePattern(editingPattern).then((ok) => { if (ok) setEditing(null); });
          } : undefined}
        />
      )}
    </>
  );
};
